import React, { useState } from "react";
import { Panel } from "../components/Panel";
import { VehiclePreview } from "../components/VehiclePreview";
import { usePlayer } from "../context/PlayerContext";
import { api, apiErr } from "../api/client";
import { VEHICLES, THEME_COLORS, COSMETIC_COLORS } from "../game/config";
import { CosmeticIcon } from "../components/CosmeticIcon";
import { Gauge, Hammer, Heart, Zap, Check, Lock, Recycle } from "lucide-react";
import { toast } from "sonner";

const SLOTS = [["skin", "Skin"], ["wheel", "Wheels"], ["trail", "Trail"], ["boost", "Boost FX"], ["smash", "Smash FX"], ["paint", "Paint"], ["title", "Title"]];

export default function Garage({ onClose, onNeedShop }) {
  const { profile, applyUser } = usePlayer();
  const [tab, setTab] = useState("cosmetics");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [recycleMode, setRecycleMode] = useState(false);

  const theme = (profile.garage?.skin || "skin_classic").replace("skin_", "");
  const tier = profile.maxEvolutionReached || 0;
  const owned = profile.ownedCosmetics || [];
  const byType = (t) => owned.filter((id) => id.startsWith(t + "_")).map((id) => ({ id, color: COSMETIC_COLORS[id] || "#888" }));

  const equip = async (slot, id) => {
    setBusy(true); setErr(null);
    try { const { data } = await api.equip(slot, id); applyUser(data.user); }
    catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); }
  };

  const recycle = async (id) => {
    if (busy) return;
    if (!window.confirm("Melt this cosmetic down into Scrap? This can't be undone.")) return;
    setBusy(true); setErr(null);
    try { const { data } = await api.salvageRecycle(id); applyUser(data.user); toast.success(`Recycled for +${data.scrap} Scrap!`); }
    catch (e) { toast.error(apiErr(e, "Could not recycle.")); }
    finally { setBusy(false); }
  };

  return (
    <Panel title="Garage" onClose={onClose} wide>
      <div className="grid md:grid-cols-2 gap-5">
        {/* preview */}
        <div className="flex flex-col items-center">
          <div className="card w-full p-4 flex flex-col items-center relative overflow-hidden">
            <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-1">{VEHICLES[tier].name}</div>
            <VehiclePreview tier={tier} theme={theme} size={200} />
          </div>
          {/* gameplay stats */}
          <div className="card w-full p-4 mt-3">
            <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold mb-3 flex items-center gap-1"><Gauge size={14} /> Gameplay Stats (Evolution)</div>
            <div className="space-y-2 text-sm">
              <StatBar icon={Hammer} label="Smash Power" color="from-orange-500 to-amber-400" val={VEHICLES[tier].smashPower} max={15} />
              <StatBar icon={Heart} label="Health" color="from-red-500 to-rose-400" val={VEHICLES[tier].maxHealth} max={850} />
              <StatBar icon={Gauge} label="Speed" color="from-cyan-500 to-blue-400" val={VEHICLES[tier].maxSpeed} max={6.4} />
              <StatBar icon={Zap} label="Boost" color="from-purple-500 to-violet-400" val={VEHICLES[tier].boostMax} max={200} />
            </div>
            <div className="text-[10px] text-slate-500 mt-3">Stats increase automatically as you evolve in-match. Cosmetics never affect gameplay.</div>
          </div>
        </div>

        {/* cosmetics */}
        <div>
          <div className="flex items-center justify-between py-2 mb-1">
            <div className="text-xs uppercase tracking-widest text-amber-400 font-bold">Cosmetics</div>
            <div className="flex items-center gap-2">
              <span className="card px-2 py-1 flex items-center gap-1 text-xs font-black text-emerald-300" data-testid="garage-scrap"><Recycle size={13} /> {(profile.scrap || 0).toLocaleString()}</span>
              <button onClick={() => setRecycleMode((v) => !v)} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold uppercase flex items-center gap-1 ${recycleMode ? "bg-emerald-500 text-black" : "bg-slate-800 text-emerald-300"}`} data-testid="garage-recycle-toggle">
                <Recycle size={13} /> {recycleMode ? "Done" : "Recycle"}
              </button>
            </div>
          </div>
          {recycleMode && <div className="text-[11px] text-emerald-300/80 mb-2">Tap any unequipped cosmetic to melt it into Scrap for the Forge.</div>}
          {err && <div className="text-red-400 text-xs mb-2">{err}</div>}
          <div className="max-h-[46vh] overflow-y-auto scroll-thin pr-1">
            {SLOTS.map(([slot, label]) => {
              const items = byType(slot);
              if (!items.length) return null;
              return (
                <CosmeticRow key={slot} title={label} slot={slot} items={items}
                  owned={owned} equipped={profile.garage?.[slot]} onEquip={equip} busy={busy}
                  recycleMode={recycleMode} onRecycle={recycle} />
              );
            })}
          </div>
          <div className="text-[11px] text-slate-500 mt-3">Unlock more skins, wheels, trails, smash & boost effects in the Shop. Cosmetics never affect gameplay.</div>
          <button onClick={onNeedShop} className="btn-ghost w-full py-2.5 mt-2" data-testid="garage-open-shop">Open Shop</button>
        </div>
      </div>
    </Panel>
  );
}

function StatBar({ icon: Icon, label, color, val, max }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} className="text-slate-400 shrink-0" />
      <span className="w-24 text-slate-300 text-xs">{label}</span>
      <div className="flex-1 h-2.5 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${color}`} style={{ width: `${Math.min(100, (val / max) * 100)}%` }} />
      </div>
    </div>
  );
}

function CosmeticRow({ title, slot, items, owned, equipped, onEquip, busy, recycleMode, onRecycle }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-2">{title}</div>
      <div className="grid grid-cols-3 gap-2">
        {items.map((it) => {
          const has = owned.includes(it.id);
          const on = equipped === it.id;
          const canRecycle = recycleMode && has && !on;
          const handleClick = () => {
            if (recycleMode) { if (canRecycle) onRecycle(it.id); }
            else if (has) onEquip(slot, it.id);
          };
          return (
            <button key={it.id} disabled={busy || !has || (recycleMode && on)} onClick={handleClick}
              className={`card cos-tile p-3 flex flex-col items-center gap-2 relative ${on ? "border-amber-500 border-2" : ""} ${!has ? "opacity-50" : canRecycle ? "hover:border-emerald-500/70" : "hover:border-cyan-500/50"}`}
              data-testid={`cosmetic-${it.id}`}>
              <CosmeticIcon type={slot} color={it.color} size={38} />
              <span className="text-[10px] text-slate-300 capitalize">{it.id.split("_")[1]}</span>
              {on && !recycleMode && <span className="absolute top-1 right-1 text-amber-400"><Check size={14} /></span>}
              {!has && <span className="absolute top-1 right-1 text-slate-500"><Lock size={12} /></span>}
              {recycleMode && canRecycle && <span className="absolute top-1 right-1 text-emerald-400" data-testid={`recycle-${it.id}`}><Recycle size={14} /></span>}
              {recycleMode && on && <span className="absolute top-1 right-1 text-slate-500 text-[8px] font-bold uppercase">Equipped</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
